import React, { useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { useNavigate, useParams } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import ActionPanel from './EmployeeApplicationActionPanel'
import LogsComponent from '../../../components/Logs/LogsComponent'
import SuitabilityNotes from './SuitabilityNotes'
import References from './References'
import Interviews from './Interviews'
import { getStageMeta } from '../stageMeta'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'
import {
  InfoList,
  StatTile,
} from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import { employmentGaps } from '../applicationHistory'
import {
  AddressSection,
  DbsSection,
  DrivingSection,
  EmploymentSection,
  PersonalSection,
  QualificationsSection,
  RightToWorkSection,
  StatementSection,
} from './ApplicationSections'

// References.jsx imports these from here
export { AccordComponent, Line } from './ApplicationDetailKit'

const hasResponse = (reference) =>
  Object.keys(reference?.response ?? {}).length > 0

const Hero = ({ application, actionPanelRef }) => {
  const firstName =
    application?.userAccount?.firstName ?? application?.userInfo?.firstName
  const lastName =
    application?.userAccount?.lastName ?? application?.userInfo?.lastName
  const email = application?.userAccount?.email ?? application?.userInfo?.email
  const phone = application?.employee?.personnelInfo?.phoneNumber1
  const stage = application.stage ?? application.state
  const stageMeta = getStageMeta(stage)

  const referencesIn = ['reference1', 'reference2'].filter((key) =>
    hasResponse(application[key]),
  ).length
  const interviews = (application.interviews ?? []).filter(
    (interview) => interview.outcome !== 'Cancelled',
  )
  const nextInterview = interviews
    .filter(
      (interview) =>
        interview.outcome === 'Pending' &&
        moment(interview.scheduledStart).isAfter(moment()),
    )
    .sort((a, b) => moment(a.scheduledStart) - moment(b.scheduledStart))[0]
  const jobs = application.workExperience ?? []
  const gaps = employmentGaps(jobs)
  const driving = application.driving ?? {}

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <Stack gap={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" gap={2} alignItems="center" minWidth={0}>
            <Avatar
              src={application?.userAccount?.photograph}
              sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}
            >
              {firstName?.[0]}
              {lastName?.[0]}
            </Avatar>
            <Box minWidth={0}>
              <Typography variant="h5" component="h1" noWrap>
                {firstName} {lastName}
              </Typography>
              <Typography variant="body2">
                Applying for{' '}
                <Box component="span" fontWeight={600} color="text.primary">
                  {application?.employee?.clinicalRole || 'an unspecified role'}
                </Box>
              </Typography>
              <Stack
                direction="row"
                columnGap={2}
                rowGap={0.25}
                flexWrap="wrap"
                mt={0.5}
              >
                {email && (
                  <Link
                    href={`mailto:${email}`}
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <EmailOutlinedIcon sx={{ fontSize: 16 }} />
                    {email}
                  </Link>
                )}
                {phone && (
                  <Link
                    href={`tel:${phone}`}
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PhoneOutlinedIcon sx={{ fontSize: 16 }} />
                    {phone}
                  </Link>
                )}
              </Stack>
            </Box>
          </Stack>
          <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} gap={0.5}>
            <Chip
              sx={{ borderRadius: 1, fontWeight: 600 }}
              color={stageMeta.color}
              label={stage}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign={{ sm: 'right' }}
              maxWidth={260}
            >
              {stageMeta.description}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                actionPanelRef.current?.scrollIntoView({ behavior: 'smooth' })
              }
              sx={{ display: { lg: 'none' } }}
            >
              Actions
            </Button>
          </Stack>
        </Stack>

        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          }}
          gap={1.5}
        >
          <StatTile
            label="References"
            value={`${referencesIn} of 2`}
            caption={referencesIn === 2 ? 'All received' : 'Awaiting response'}
            tone={referencesIn === 2 ? 'success' : 'warning'}
          />
          <StatTile
            label="Interviews"
            value={interviews.length || 'None'}
            caption={
              nextInterview
                ? `Next ${moment(nextInterview.scheduledStart).format('D MMM, HH:mm')}`
                : 'None upcoming'
            }
          />
          <StatTile
            label="Employment"
            value={
              application.noEmploymentHistory
                ? 'None declared'
                : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
            }
            caption={
              gaps.length
                ? `${gaps.length} gap${gaps.length === 1 ? '' : 's'}`
                : jobs.length
                  ? 'No gaps'
                  : undefined
            }
            tone={gaps.length ? 'warning' : jobs.length ? 'success' : undefined}
          />
          <StatTile
            label="Driving"
            value={
              driving.hasValidLicence === false
                ? 'No licence'
                : driving.hasValidLicence
                  ? [driving.hasC1 && 'C1', driving.hasBlueLight && 'Blue light']
                      .filter(Boolean)
                      .join(' · ') || 'Car licence'
                  : null
            }
            caption={
              driving.hasValidLicence
                ? driving.licenceLastEight && driving.checkCode
                  ? 'Ready for DVLA check'
                  : 'Check details missing'
                : undefined
            }
            tone={
              driving.hasValidLicence
                ? driving.licenceLastEight && driving.checkCode
                  ? 'success'
                  : 'warning'
                : undefined
            }
          />
        </Box>
      </Stack>
    </Paper>
  )
}

const EmployeeApplication = ({ setSelectedApplication }) => {
  const actionPanelRef = useRef(null)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [application, setApplication] = useState({})
  const { id: applicationID } = useParams('')
  const { setPageTitle } = useDocumentTitle()
  const { setToast } = useContext(GlobalContext)
  const [openComment, setOpenComment] = useState(false)

  const getApplication = () => {
    setLoading(true)
    API.get('/employees/applications/' + applicationID)
      .then((res) => {
        if (res.status === 200) {
          setApplication(res.data)
        } else if (res.status === 404) {
          navigate('/employees/applications')
          setToast({ message: 'Application Not Found', severity: 'error' })
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => getApplication(), [])

  useEffect(() => {
    if (application?.userAccount) {
      setPageTitle(
        `${application.userAccount?.firstName ?? ''} ${application.userAccount?.lastName ?? ''}`,
      )
    }
  }, [application?.userAccount, setPageTitle])

  // Keep the page mounted during refreshes so open sections and scroll
  // position survive an action; only the very first load shows the bar.
  if (loading && !application?._id) return <LinearProgress />

  return (
    <Grid container spacing={2}>
      {loading && (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      )}
      <Grid item xs={12}>
        <Hero application={application} actionPanelRef={actionPanelRef} />
      </Grid>
      <Grid item xs={12} lg={8}>
        <Stack>
          <PersonalSection application={application} />
          <AddressSection application={application} />
          <EmploymentSection application={application} />
          <DrivingSection application={application} />
          <RightToWorkSection application={application} />
          <QualificationsSection application={application} />
          <DbsSection application={application} />
          <StatementSection application={application} />
          <References application={application} getApplication={getApplication} />
        </Stack>
        <Box marginTop={2}>
          <Interviews application={application} getApplication={getApplication} />
        </Box>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Stack gap={2}>
          <ActionPanel
            application={application}
            actionPanelRef={actionPanelRef}
            setSelectedApplication={setSelectedApplication}
            getApplication={getApplication}
          />
          <Paper sx={{ px: 2, py: 1 }}>
            <InfoList
              rows={[
                {
                  label: 'Started',
                  value: moment(application.createdAt).format('D MMM YYYY'),
                },
                {
                  label: 'Last active',
                  value: moment(
                    application?.userAccount?.lastActive ?? application.createdAt,
                  ).format('D MMM YYYY, HH:mm'),
                },
                ...(application.offerExtended
                  ? [
                      {
                        label: 'Offer extended',
                        value: moment(application.offerExtended).format(
                          'D MMM YYYY',
                        ),
                      },
                    ]
                  : []),
                ...(application.offerAccepted || application.offerRejected
                  ? [
                      {
                        label: 'Offer response',
                        value: `${
                          application.offerAccepted ? 'Accepted' : 'Declined'
                        }${
                          application.offerRespondedAt
                            ? ' · ' +
                              moment(application.offerRespondedAt).format(
                                'D MMM YYYY',
                              )
                            : ''
                        }`,
                      },
                    ]
                  : []),
                ...(application.state === 'Rejected' &&
                application.rejectionReason
                  ? [
                      {
                        label: 'Rejection reason',
                        value: application.rejectionReason,
                      },
                    ]
                  : []),
              ]}
            />
          </Paper>
          <SuitabilityNotes
            initialValue={application.suitabilityNotes ?? ''}
            applicationID={application._id}
            getApplication={getApplication}
          />
          <Stack gap={1} component={Paper} padding={1}>
            <LogsComponent
              title="Note"
              targetId={application._id}
              targetType="employeeApplication"
              allowCreateLog
              openComment={openComment}
              setOpenComment={setOpenComment}
            />
          </Stack>
        </Stack>
      </Grid>
    </Grid>
  )
}

export default EmployeeApplication
